import { format } from 'date-fns';
import { Star } from 'lucide-react';
import { RATING_META, type HireFeedback } from '@/lib/hire-rating';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/* ============================================================================
 * Compact brand-rating stars for a table row (Creator Payments), with a
 * premium hover card carrying the brand's written reason — if there is one.
 * ============================================================================
 * Deliberately terse inline: five small stars, no number, no label — the row
 * already carries a lot (status, deadline, videos, payment). The score,
 * label, and the brand's actual words only surface on hover, in the same
 * rating-tone the full HireFeedbackCard uses (see lib/hire-rating), so this
 * reads as the same feature in miniature rather than a second design.
 *
 * No tooltip at all when there's nothing to show in it — an empty hover card
 * is worse than no affordance, and a brand rates plenty of hires with no memo
 * attached. Stars still render in that case; only the hover goes away.
 * ========================================================================== */

function StarRow({ rating, starClass, size = 'h-3.5 w-3.5' }: { rating: number; starClass: string; size?: string }) {
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          className={n <= rating ? `${size} ${starClass}` : `${size} text-muted-foreground/25`}
          fill={n <= rating ? 'currentColor' : 'none'}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export function HireRatingStars({ feedback }: { feedback: HireFeedback | null | undefined }) {
  // No rating on this hire at all — nothing to show, not even a placeholder;
  // most rows won't have one yet and a dash for every unrated row is noise.
  if (!feedback) return null;

  const meta = RATING_META[feedback.rating];
  const stars = <StarRow rating={feedback.rating} starClass={meta.star} />;
  const hasNote = !!feedback.notes?.trim();

  if (!hasNote) return stars;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Not a button — stars are informational, and this cell sits inside
              a row whose own onClick navigates to the hire. Widening the hit
              target with padding, not a focusable element, keeps that intact. */}
          <div className="inline-flex cursor-default -m-1 p-1">{stars}</div>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className={`w-64 max-w-[80vw] overflow-hidden rounded-lg border p-0 shadow-lg ${meta.border}`}
        >
          <div className={`flex items-center justify-between gap-2 px-3 py-2 ${meta.medallion}`}>
            <span className={`text-sm font-bold leading-none ${meta.score}`}>
              {feedback.rating}.0 <span className="font-medium opacity-80">· {meta.label}</span>
            </span>
            <StarRow rating={feedback.rating} starClass={meta.star} size="h-3 w-3" />
          </div>
          <div className="space-y-1.5 px-3 py-2.5">
            <p className="text-xs leading-relaxed text-foreground whitespace-pre-wrap break-words">
              {feedback.notes}
            </p>
            {(feedback.created_at || feedback.submitted_by_email) && (
              <p className="text-[10px] text-muted-foreground">
                {feedback.created_at ? format(new Date(feedback.created_at), 'MMM d, yyyy') : ''}
                {feedback.created_at && feedback.submitted_by_email ? ' · ' : ''}
                {feedback.submitted_by_email ?? ''}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
